// LB-355 — changing what a token is worth (Alan, 20–21 Sep; canvas U32CGrk98PhFGKerhQwEb5).
//
// A rate change asks the parent TWO separate questions, because what a child has already
// earned and what they will earn from now on are independent:
//
//   balances  "money"   resize every child's token balance so its £ value is unchanged
//             "tokens"  leave the count as it is, so its £ value moves with the rate
//   rewards   "rebalance"  run the existing Rebalance (repriceToCap) for every child against
//                          their cap at the NEW rate
//             "keep"       change no reward at all
//
// Both are forced choices with nothing preselected; this module refuses to apply without
// both. Everything that is money keeps its money value whatever the answers: goals (stored in
// pence, see goalTargetTokens), the weekly cap (stored in pounds), the Savings Vault (pence),
// and the price of every reward a child spends on (experienceRewards, Alan 21 Sep: "should be
// scaled, too"), which is resized so its £ price holds. Oinks are never money and never move.
//
// Pending requests carry token amounts already earned or already asked for, so they follow
// the BALANCE answer (Alan, 21 Sep: "should follow the choice the parent makes"): resized under
// "money", untouched under "tokens". That includes a pending goal redemption, which under
// "tokens" stays at the count the child asked with.
//
// Resizing rounds to the nearest token (LB-43). Every resized balance writes one tagged
// transaction (rateResize) beside the existing rate marker, and a child whose count changed,
// or whose top goal's token target moved, is left a one-off note for their next visit.
//
// Pure: no React, no DOM. applyRateChange MUTATES the draft it is given (call it inside
// update()); previewRateChange runs the same function on a copy, so what the parent is shown
// before confirming is, by construction, what confirming does.

import { repriceToCap, capTokensFor } from "./spendingDefaults.js";
import { goalPricePence, goalProgress, goalTargetTokens, logSetting, penceToTokens, pickTopGoal, rateMarkerTxn, tokensToPence } from "./utils.js";

export const BALANCE_ANSWERS = ["money", "tokens"];
export const REWARD_ANSWERS = ["rebalance", "keep"];

const rateOf = (s) => (s?.settings?.exchangeRate > 0 ? s.settings.exchangeRate : 10);

// Nearest token. A positive amount never resizes to nothing.
export function resizeTokens(n, fromRate, toRate) {
  if (typeof n !== "number" || !isFinite(n)) return n;
  const out = Math.round((n * toRate) / fromRate);
  return n > 0 ? Math.max(1, out) : out;
}

// Every value a child is paid, in the groups the detail view draws: the weekly reward for
// daily tasks, earning activities, and Learn & Earn (each game's full and pass reward, for the
// games that are switched on). `key` is stable across a before/after pair so the two can be
// lined up; a task Rebalance removed is present before and absent after.
export function earningRows(state, childId) {
  const child = (state.profiles || []).find((p) => p.id === childId);
  if (!child) return [];
  const rows = [];
  rows.push({ group: "habit", key: "habitReward", tokens: child.habitReward ?? state.settings?.defaultHabitReward ?? 20 });
  (state.activities || []).filter((a) => a.childId === childId).forEach((a) => rows.push({ group: "tasks", key: `task:${a.id}`, label: a.label, tokens: a.tokens || 0 }));
  const lc = (state.learningContent || {})[childId] || {};
  const games = [
    ["spellingEnabled", "spellingReward", "spellingPassReward"],
    ["vocabEnabled", "vocabFullReward", "vocabPassReward"],
    ["ttstreaksEnabled", "ttStreakFullReward", "ttStreakPassReward"],
    ["mathsworkoutEnabled", "mathsFullReward", "mathsPassReward"],
  ];
  for (const [on, full, pass] of games) {
    if (lc[on] === false) continue;
    if (typeof lc[full] === "number") rows.push({ group: "learn", key: full, tokens: lc[full] });
    if (typeof lc[pass] === "number") rows.push({ group: "learn", key: pass, tokens: lc[pass] });
  }
  return rows;
}

// The rewards a child SPENDS on: household-wide ones and their own.
export function spendRows(state, childId) {
  return (state.experienceRewards || []).filter((r) => !r.childId || r.childId === childId).map((r) => ({ key: `spend:${r.id}`, label: r.label, tokens: r.tokens }));
}

function kidNoteFor(child, before, after, fromRate, toRate, topBefore, nowIso) {
  const goal = topBefore
    ? { title: topBefore.title, pence: topBefore.pence, before: topBefore.tokens, after: goalTargetTokens(topBefore.goal, toRate) }
    : null;
  if (before !== after) {
    return { kind: "tokens", fromRate, toRate, before, after, penceBefore: tokensToPence(before, fromRate), penceAfter: tokensToPence(after, toRate), goal, at: nowIso };
  }
  if (goal && goal.before !== goal.after) {
    const gp = goalProgress(child, goal.after, toRate);
    return { kind: "goal", fromRate, toRate, tokens: after, have: gp.combined, goal, at: nowIso };
  }
  return null;
}

/* MUTATES `draft`. Returns { changed, fromRate, toRate, children, rebalanced }.
 *
 * opts: { toRate, balances, rewards, deviceId, acting, nowIso, newId, resizeReason }
 *   resizeReason is the words on the tagged row, supplied by the caller so the copy lives in
 *   a component's ct() and Content Studio can place it.
 */
export function applyRateChange(draft, opts) {
  const { toRate, balances, rewards, deviceId = null, acting = null, nowIso = new Date().toISOString(), newId, resizeReason = "Token value changed" } = opts || {};
  const fromRate = rateOf(draft);
  if (!BALANCE_ANSWERS.includes(balances) || !REWARD_ANSWERS.includes(rewards)) throw new Error("applyRateChange: both answers are required");
  if (!(toRate > 0) || toRate === fromRate) return { changed: false, fromRate, toRate, children: [], rebalanced: {} };
  const id = typeof newId === "function" ? newId : () => Math.random().toString(36).slice(2, 12);

  // What each child's top goal needed BEFORE, for the note. Read at the old rate.
  const topBefore = {};
  for (const c of draft.profiles || []) {
    const g = pickTopGoal(c.goals, c, fromRate);
    if (g) topBefore[c.id] = { goal: g, title: g.title, pence: goalPricePence(g, fromRate), tokens: goalTargetTokens(g, fromRate) };
  }

  logSetting(draft, deviceId, "exchangeRate", `${fromRate} = £1`, `${toRate} = £1`, acting);
  draft.transactions.unshift(rateMarkerTxn(deviceId, fromRate, toRate));
  draft.settings.exchangeRate = toRate;

  // 1. Balances, and the pending requests that carry already-earned or already-asked tokens.
  const children = [];
  for (const c of draft.profiles || []) {
    const before = c.tokenBalance || 0;
    const after = balances === "money" ? resizeTokens(before, fromRate, toRate) : before;
    if (after !== before) {
      c.tokenBalance = after;
      const delta = after - before;
      draft.transactions.unshift({ id: id(), deviceId, childId: c.id, amount: Math.abs(delta), reason: resizeReason, type: delta > 0 ? "credit" : "debit", balance: "token", timestamp: nowIso, rateResize: { fromRate, toRate, before, after } });
    }
    children.push({ id: c.id, name: c.name, before, after, penceBefore: tokensToPence(before, fromRate), penceAfter: tokensToPence(after, toRate) });
  }
  if (balances === "money") {
    for (const r of draft.requests || []) {
      if (!r || r.status !== "pending" || typeof r.requestedAmount !== "number") continue;
      r.requestedAmount = resizeTokens(r.requestedAmount, fromRate, toRate);
      // LB-17: an ask carries its £ price exactly as typed, so re-derive the tokens from it
      // rather than resizing the rounded figure (LB-355: a pending ask follows the money).
      if (r.requestType === "ask_to_buy" && typeof r.pricePence === "number" && r.pricePence > 0) r.requestedAmount = penceToTokens(r.pricePence, toRate);
      if (typeof r.baseAmount === "number") r.baseAmount = resizeTokens(r.baseAmount, fromRate, toRate);
      // A pending transfer to the vault has already moved: its token row and the vault row's
      // record of the tokens both move with it, so rejecting it puts back the resized count.
      if (r.requestType === "vault_transfer") {
        for (const t of draft.transactions) {
          if (t.requestId !== r.id || t.status !== "pending") continue;
          if (t.balance === "token" && typeof t.amount === "number") t.amount = resizeTokens(t.amount, fromRate, toRate);
          if (typeof t.tokens === "number") t.tokens = resizeTokens(t.tokens, fromRate, toRate);
        }
      }
    }
  }

  // 2. What a child spends on keeps its price in money, whatever the answers.
  for (const r of draft.experienceRewards || []) {
    if (typeof r.tokens === "number" && r.tokens > 0) r.tokens = resizeTokens(r.tokens, fromRate, toRate);
  }

  // 3. Rewards from now on. Rebalance runs after the rate is set, so each cap is read in
  //    tokens at the NEW rate — the cap's money value never moves.
  const rebalanced = {};
  if (rewards === "rebalance") {
    for (const c of draft.profiles || []) rebalanced[c.id] = repriceToCap(draft, c.id);
  }

  // 4. The one-off note for each child's next visit. A newer change replaces an unseen older
  //    one: the older note's numbers are no longer true.
  for (const c of draft.profiles || []) {
    const row = children.find((x) => x.id === c.id);
    const note = kidNoteFor(c, row.before, row.after, fromRate, toRate, topBefore[c.id], nowIso);
    if (note) c.pendingRateNote = note;
    else delete c.pendingRateNote;
  }

  return { changed: true, fromRate, toRate, children, rebalanced };
}

// What a change WOULD do, without doing it. The detail view and every per-child figure the
// three steps show are read from this, so preview and apply cannot disagree.
export function previewRateChange(state, toRate, { balances, rewards }) {
  const before = JSON.parse(JSON.stringify(state));
  const after = JSON.parse(JSON.stringify(state));
  let n = 0;
  const result = applyRateChange(after, { toRate, balances, rewards, deviceId: "preview", nowIso: new Date().toISOString(), newId: () => `preview-${++n}` });
  const fromRate = rateOf(before);
  const perChild = (before.profiles || []).map((c) => {
    const a = (after.profiles || []).find((x) => x.id === c.id) || c;
    return {
      id: c.id,
      name: c.name,
      balance: { before: c.tokenBalance || 0, after: a.tokenBalance || 0, penceBefore: tokensToPence(c.tokenBalance || 0, fromRate), penceAfter: tokensToPence(a.tokenBalance || 0, toRate) },
      earning: { before: earningRows(before, c.id), after: earningRows(after, c.id) },
      spend: { before: spendRows(before, c.id), after: spendRows(after, c.id) },
      goals: (c.goals || []).map((g) => ({
        id: g.id,
        title: g.title,
        pence: goalPricePence(g, fromRate),
        before: goalTargetTokens(g, fromRate),
        after: goalTargetTokens(g, toRate),
        pctBefore: goalProgress(c, goalTargetTokens(g, fromRate), fromRate).pct,
        pctAfter: goalProgress(a, goalTargetTokens(g, toRate), toRate).pct,
      })),
      cap: { gbp: c.spendingCap && c.spendingCap.enabled !== false ? c.spendingCap.weeklyGBP : null, before: capTokensFor(c, fromRate), after: capTokensFor(a, toRate) },
      vaultPence: c.vaultBalancePence || 0,
      note: a.pendingRateNote || null,
      rebalance: result.rebalanced[c.id] || null,
    };
  });
  return { fromRate, toRate, perChild };
}
